import { useState, type FormEvent } from "react";
import { MIN_PASSWORD_LENGTH, type Account as AccountInfo, type Me } from "@ai-rpg/protocol";
import { changePassword, signOut } from "../api.ts";
import { Tank, usageWords } from "./Meter.tsx";

const problemOf = (err: unknown) => (err instanceof Error ? err.message : String(err));

interface Props {
  readonly account: AccountInfo;
  /** How much of the month's play is used, where the account has a limit. */
  readonly usage: Me["usage"] | null;
  readonly onSignedOut: () => void;
}

/** One's own account: who you are here, a new password, and the way out. */
export function Account({ account, usage, onSignedOut }: Props) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const short = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const differ = again.length > 0 && again !== next;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setDone(false);
    try {
      await changePassword(current, next);
      setCurrent("");
      setNext("");
      setAgain("");
      setDone(true);
    } catch (err) {
      setProblem(problemOf(err));
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    try {
      await signOut();
    } finally {
      onSignedOut();
    }
  };

  return (
    <main className="account">
      <header className="page-head">
        <div>
          <h1 className="page-title">{account.username}</h1>
          <p className="page-summary">
            {account.role === "admin" ? "admin" : "player"}
            {account.canPlay ? "" : " · can't start games"}
            {" · "}
            <a href="/terms" target="_blank" rel="noreferrer">
              Terms of use
            </a>
          </p>
        </div>
        <div className="page-actions">
          <button className="button" onClick={() => void leave()}>
            Sign out
          </button>
        </div>
      </header>

      {usage && (
        <section className="form form-narrow" aria-label="This month's play">
          <h2 className="section-label">This month's play</h2>
          <Tank usage={usage} wide />
          <p className="field-note">
            {usage.percent}% used. {usageWords(usage)} Games you host count toward it; at someone else's table it is
            theirs that is used.
          </p>
        </section>
      )}

      <form className="form form-narrow" onSubmit={submit}>
        <h2 className="section-label">A new password</h2>
        <label className="field">
          <span className="field-label">Your password now</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">The new one</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <span className="field-note">
            At least {MIN_PASSWORD_LENGTH} characters. A few words you'll remember do better than one clever one.
          </span>
        </label>
        <label className="field">
          <span className="field-label">The new one again</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
          {differ && <span className="problem">These two don't match yet.</span>}
        </label>
        <button
          className="button button-primary"
          type="submit"
          disabled={busy || !current || !next || short || again !== next}
        >
          {busy ? "Changing…" : "Change password"}
        </button>
        {done && <p className="field-note">Changed. Any other browser you were signed in on has been signed out.</p>}
        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </main>
  );
}
